import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import * as XLSX from 'xlsx';
import PptxGenJS from 'pptxgenjs';
import './styles/TableComponent.css';

function TableComponent({ cols, statements, datavalue, lvl1, lvl2, lvl3, lvl4, flt3, flt4, displaySettings }) {
  const [showChart, setShowChart] = useState(false);
  const [isWeighted, setIsWeighted] = useState(false);
  const [isSigtest, setIsSigtest] = useState(false);

  const handleToggle = () => setShowChart(!showChart);
  const handleToggleWeighting = () => setIsWeighted(!isWeighted);
  const handleToggleSigtest = () => setIsSigtest(!isSigtest);

  const downloadExcel = () => {
    const workSheet = XLSX.utils.json_to_sheet(datavalue);
    const workBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workBook, workSheet, "Data");
    XLSX.writeFile(workBook, "data.xlsx");
  };

  const downloadPPT = () => {
    let pptx = new PptxGenJS();
    let slide = pptx.addSlide();
    slide.addChart(
      pptx.ChartType.bar, 
      chartData.datasets.map(ds => ({
        name: ds.label,
        labels: chartData.labels,
        values: ds.data
      }))
    );
    pptx.writeFile({ fileName: "chart.pptx" });
  };

  const chartData = {
    labels: statements.filter(st => st.index !== -98).map(st => st.label),
    datasets: cols.map((col, idx) => ({
      label: col.label,
      data: statements.filter(st => st.index !== -98).map(st => {
        const item = datavalue.find(
          d => d[lvl1] === col.index && 
               d[lvl2] === st.index && 
               d[lvl3] === flt3 && 
               d[lvl4] === flt4
        );
        return item && item.data.perc ? parseFloat(item.data.perc.replace('%', '')) : 0;
      }),
      backgroundColor: `hsla(${(idx * 360) / cols.length}, 70%, 60%, 0.7)`,
      borderColor: `hsla(${(idx * 360) / cols.length}, 70%, 50%, 1)`,
      borderWidth: 2,
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          padding: 15,
          font: {
            size: 12,
            weight: 600
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        borderColor: '#667eea',
        borderWidth: 1
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    }
  };

  return (
    <div className="table-component">
      <div className="button-group">
        <button onClick={handleToggle}>
          {showChart ? '📊 Show Table' : '📈 Show Chart'}
        </button>
        <button onClick={handleToggleWeighting}>
          {isWeighted ? '⚖️ Show Unweighted' : '⚖️ Show Weighted'}
        </button>
        <button onClick={handleToggleSigtest}>
          {isSigtest ? '🔬 Hide Sig Test' : '🔬 Show Sig Test'}
        </button>
        <button onClick={showChart ? downloadPPT : downloadExcel}>
          {showChart ? '💾 Download PPT' : '💾 Download Excel'}
        </button>
      </div>

      {showChart ? (
        <div className="chart-container" style={{ height: '500px' }}>
          <Bar data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: "250px" }}></th>
                {cols && cols.map(col => (
                  <th key={col.index} style={{ maxWidth: "80px" }}>
                    {col.label}
                    {col.letter && (<div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '4px' }}>
                      [{col.letter}]
                    </div>)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {statements && statements.map((st, stIdx) => (
                <React.Fragment key={`statement-${st.index}-${stIdx}`}>
                  {displaySettings && displaySettings.showFrequency && (
                    <tr>
                      <td>{st.label ? st.label : ''}</td>
                      {cols && cols.map((col, colIdx) => {
                        const item = datavalue.find(
                          d => d[lvl1] === col.index && 
                               d[lvl2] === st.index && 
                               d[lvl3] === flt3 && 
                               d[lvl4] === flt4
                        );
                        return (
                          <td key={`freq-${col.index}-${colIdx}`}>
                        
                            {st.index > -100 ? (
                              item ? (isWeighted ? `${item.data.freqwt}` : `${item.data.freq}`) : '-'
                            ) : (
                              item ? `${item.data.Mean}` : '-'
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  
                  {st.index > -100 && st.index != -98 && displaySettings && displaySettings.showPercent && (
                    <tr>
                      <td>
                        {st.label && displaySettings && !displaySettings.showFrequency ? st.label : ''}
                      </td>
                      {cols && cols.map((col, colIdx) => {
                        const item = datavalue.find(
                          d => d[lvl1] === col.index && 
                               d[lvl2] === st.index && 
                               d[lvl3] === flt3 && 
                               d[lvl4] === flt4
                        );
                        return (
                          <td key={`perc-${col.index}-${colIdx}`}>
                            {item ? (isWeighted ? `${item.data.percwt}` : `${item.data.perc}`) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  
                  {st.index > -100 && displaySettings && isSigtest && (
                    <tr style={{ background: 'rgba(102, 126, 234, 0.03)' }}>
                      <td style={{ fontStyle: 'italic', color: '#667eea', fontSize: '0.813rem' }}>
                        {displaySettings.showFrequency || displaySettings.showPercent ? 'Sig Test' : st.label}
                      </td>
                      {cols && cols.map((col, colIdx) => {
                        const item = datavalue.find(
                          d => d[lvl1] === col.index && 
                               d[lvl2] === st.index && 
                               d[lvl3] === flt3 && 
                               d[lvl4] === flt4
                        );
                        return (
                          <td 
                            key={`sig-${col.index}-${colIdx}`}
                            style={{ 
                              fontWeight: '700', 
                              color: '#764ba2',
                              fontSize: '0.813rem',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {item && item.data.sig ? 
                              `${item.data.sig.map(sig => sig.colSymbol).join('')}` : 
                              ''
                            }
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TableComponent;